import React, { useEffect, useState } from 'react';
import { styles } from './RecentlyViewed.css';
import { ProductCarousel } from '@jbhi-fi/components/src/ProductCarousel/ProductCarousel';
import { useInterval } from '@jbhi-fi/utils/src/useInterval/useInterval';
import DomFont from '@jbhi-fi/lanyard-ui/font/DomFont';
import { AlgoliaProductHit } from '../../../../utils/types';
import { ProductCard } from '@jbhi-fi/components/src/ProductCard';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { useProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { useProductCompare } from '@jbhi-fi/components/src/ProductCompare';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { getStoreCurrency } from '../../../../utils/shopify/getStoreCurrency';
import { SmartModuleSettings } from '../../../../../global';
import { useAlternativesContext } from '@jbhi-fi/components/src/ProductAlternatives/AlternativesContextProvider';

const recentlyViewedProducts = JSON.parse(localStorage.getItem('recentlyViewed') as string);
const sortByMostRecent = (a: AlgoliaProductHit, b: AlgoliaProductHit) => {
  const skuA = a.sku || a.skus?.[0];
  const skuB = b.sku || b.skus?.[0];
  return recentlyViewedProducts.indexOf(Number(skuA)) - recentlyViewedProducts.indexOf(Number(skuB));
};

type RecentlyViewedProps = {
  data?: Array<AlgoliaProductHit>;
  settings?: SmartModuleSettings;
  page: string;
  isEnhancedGoogleRecommendationsEnabled?: boolean;
};

const RecentlyViewed = ({
  data,
  settings,
  page,
  isEnhancedGoogleRecommendationsEnabled,
}: RecentlyViewedProps): JSX.Element => {
  const filteredAlgoliaData = data?.sort(sortByMostRecent) ?? [];
  const [showRecentlyViewed, setShowRecentlyViewed] = useState(settings?.isEnabled ?? false);
  const hasLoaded = data?.length;
  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
  const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const { handleViewAlternatives } = useAlternativesContext();
  const isAlternativesEnabled = settings?.isAlternativesEnabled && isEnhancedGoogleRecommendationsEnabled;

  const handleLoadingTimeout = () => {
    if (!hasLoaded) {
      setShowRecentlyViewed(false);
    }
  };

  useInterval(handleLoadingTimeout, window.smartModuleSettings?.loadingTimeout || 5000, true);

  useEffect(() => {
    const productImpressions = filteredAlgoliaData?.map((hit, i) => ({
      ...hit.google_tag_manager,
      list: 'dynamic-recentlyViewed',
      position: i,
    }));

    if (productImpressions?.length) {
      window.gtm.track('productImpression', {
        products: productImpressions,
      });
    }
  }, [data]);

  return (
    <>
      {showRecentlyViewed && filteredAlgoliaData.length ? (
        <div className={styles.recentlyViewedContainer} data-testid={`${page}-recently-viewed-products-container`}>
          <DomFont className={styles.heading} testId={`${page}-recently-viewed-products-title`}>
            {settings?.title}
          </DomFont>
          <ProductCarousel
            fullBleed={false}
            trackingListName={settings?.title || '(unavailable)'}
            className={styles.carousel}
            fullHeightItems
            products={filteredAlgoliaData}
            renderProduct={({ hit, position }) => (
              <ProductCard
                {...AlgoliaHitTransformer.toProductCardProps(hit, {
                  trackingDescriptor: {
                    page,
                    group: 'recentlyViewed',
                    currency: getStoreCurrency(),
                    ecommerceExtraProps: {
                      list: 'dynamic-recentlyViewed',
                      position,
                    },
                  },
                })}
                {...productCartPropsOverrides}
                variant={isEnhancedGoogleRecommendationsEnabled ? 'minified' : 'tablet'}
                getIsCompared={isAddedToCompare}
                onCompare={compareHandleClick}
                getShouldShowCompare={shouldShowCompare}
                onUpdateWishlist={handleUpdateWishlist}
                getIsInWishlist={getIsInWishlist}
                isLoggedIn={isLoggedIn}
                onViewAlternatives={isAlternativesEnabled ? () => handleViewAlternatives(hit) : undefined}
                onAddToCart={
                  onAddToCart
                    ? (e, _sku, variantId, eventData) => onAddToCart(e, hit, variantId, eventData?.trackingDescriptor)
                    : undefined
                }
              />
            )}
          />
        </div>
      ) : null}
    </>
  );
};

export default RecentlyViewed;
